import { useEffect, useState } from "react";
import { Outlet, useNavigate, NavLink } from "react-router-dom";
import { ChefHat, LayoutDashboard, Mail, LogOut, ShieldCheck, Users, Activity, UserPlus, BrainCircuit, Coins, Shield, ListTree, WheatOff } from "lucide-react";
import { toast } from "sonner";
import { adminFetch } from "./adminUtils";

export default function AdminLayout() {
  const navigate = useNavigate();
  const [admin, setAdmin] = useState(null);

  useEffect(() => {
    // Verify admin auth via cookie-based session
    adminFetch("/api/auth/me")
      .then((u) => {
        if (u.role !== "superadmin") {
          navigate("/admin/login", { replace: true });
        } else {
          setAdmin(u);
        }
      })
      .catch(() => navigate("/admin/login", { replace: true }));
  }, [navigate]);

  const handleLogout = () => {
    adminFetch("/api/admin/logout", { method: "POST" }).catch(() => {});
    toast.success("Sesión cerrada");
    navigate("/admin/login");
  };

  if (!admin) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 flex" data-testid="admin-layout">
      {/* Sidebar */}
      <aside className="w-56 bg-slate-900 border-r border-slate-800 flex flex-col flex-shrink-0">
        {/* Brand */}
        <div className="px-5 py-5 border-b border-slate-800">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 bg-orange-500 rounded-lg flex items-center justify-center">
              <ChefHat className="w-4 h-4 text-white" />
            </div>
            <div>
              <p className="text-white font-bold text-sm leading-none">MenuGenius</p>
              <div className="flex items-center gap-1 mt-0.5">
                <ShieldCheck className="w-2.5 h-2.5 text-orange-400" />
                <p className="text-orange-400 text-[10px] uppercase tracking-wider font-semibold">Admin</p>
              </div>
            </div>
          </div>
        </div>

        {/* Nav */}
        <nav className="flex-1 px-3 py-4 space-y-1">
          <NavLink to="/admin" end data-testid="admin-nav-dashboard"
            className={({ isActive }) =>
              `flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors ${
                isActive ? "bg-orange-500/15 text-orange-400 font-medium"
                         : "text-slate-400 hover:text-slate-200 hover:bg-slate-800"}`}>
            <LayoutDashboard className="w-4 h-4" />
            Dashboard
          </NavLink>
          <NavLink to="/admin/users" data-testid="admin-nav-users"
            className={({ isActive }) =>
              `flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors ${
                isActive ? "bg-orange-500/15 text-orange-400 font-medium"
                         : "text-slate-400 hover:text-slate-200 hover:bg-slate-800"}`}>
            <Users className="w-4 h-4" />
            Usuarios
          </NavLink>
          <NavLink to="/admin/invitations" data-testid="admin-nav-invitations"
            className={({ isActive }) =>
              `flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors ${
                isActive ? "bg-orange-500/15 text-orange-400 font-medium"
                         : "text-slate-400 hover:text-slate-200 hover:bg-slate-800"}`}>
            <Mail className="w-4 h-4" />
            Invitaciones
          </NavLink>
          <NavLink to="/admin/superadmins" data-testid="admin-nav-superadmins"
            className={({ isActive }) =>
              `flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors ${
                isActive ? "bg-orange-500/15 text-orange-400 font-medium"
                         : "text-slate-400 hover:text-slate-200 hover:bg-slate-800"}`}>
            <UserPlus className="w-4 h-4" />
            Admins
          </NavLink>
          <NavLink to="/admin/monitor" data-testid="admin-nav-monitor"
            className={({ isActive }) =>
              `flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors ${
                isActive ? "bg-cyan-500/15 text-cyan-400 font-medium"
                         : "text-slate-400 hover:text-slate-200 hover:bg-slate-800"}`}>
            <Activity className="w-4 h-4" />
            Monitor
          </NavLink>
          <NavLink to="/admin/tasks" data-testid="admin-nav-tasks"
            className={({ isActive }) =>
              `flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors ${
                isActive ? "bg-cyan-500/15 text-cyan-400 font-medium"
                         : "text-slate-400 hover:text-slate-200 hover:bg-slate-800"}`}>
            <ListTree className="w-4 h-4" />
            Tareas
          </NavLink>
          <NavLink to="/admin/allergens" data-testid="admin-nav-allergens"
            className={({ isActive }) =>
              `flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors ${
                isActive ? "bg-cyan-500/15 text-cyan-400 font-medium"
                         : "text-slate-400 hover:text-slate-200 hover:bg-slate-800"}`}>
            <WheatOff className="w-4 h-4" />
            Alérgenos
          </NavLink>
          <NavLink to="/admin/ai-usage" data-testid="admin-nav-ai-usage"
            className={({ isActive }) =>
              `flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors ${
                isActive ? "bg-violet-500/15 text-violet-400 font-medium"
                         : "text-slate-400 hover:text-slate-200 hover:bg-slate-800"}`}>
            <BrainCircuit className="w-4 h-4" />
            Uso IA
          </NavLink>
          <NavLink to="/admin/ai-providers" data-testid="admin-nav-ai-providers"
            className={({ isActive }) =>
              `flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors ${
                isActive ? "bg-cyan-500/15 text-cyan-400 font-medium"
                         : "text-slate-400 hover:text-slate-200 hover:bg-slate-800"}`}>
            <Shield className="w-4 h-4" />
            Proveedores IA
          </NavLink>
          <NavLink to="/admin/plans" data-testid="admin-nav-plans"
            className={({ isActive }) =>
              `flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors ${
                isActive ? "bg-violet-500/15 text-violet-400 font-medium"
                         : "text-slate-400 hover:text-slate-200 hover:bg-slate-800"}`}>
            <Coins className="w-4 h-4" />
            Planes
          </NavLink>
        </nav>

        {/* Footer */}
        <div className="px-4 py-4 border-t border-slate-800">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-7 h-7 rounded-full bg-orange-500/20 flex items-center justify-center flex-shrink-0">
              <span className="text-orange-400 text-xs font-bold">
                {admin.name?.charAt(0).toUpperCase() || "A"}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-slate-300 text-xs font-medium truncate">{admin.name}</p>
              <p className="text-muted-foreground text-[10px] truncate">{admin.email}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            data-testid="admin-logout-btn"
            className="flex items-center gap-2 text-muted-foreground hover:text-red-400 text-xs
                       transition-colors w-full px-1 py-1 rounded"
          >
            <LogOut className="w-3.5 h-3.5" />
            Cerrar sesión
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 overflow-auto">
        <Outlet />
      </main>
    </div>
  );
}
